import { ClipboardCheck, Check, X, Minus } from "lucide-react";

const eur = (n) => (n || n === 0) ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n) : null;
const yn = (v) => v === true ? "Sì" : v === false ? "No" : null;
const txt = (v) => (v === null || v === undefined || v === "" ? null : String(v));

const Val = ({ v, bad }) => {
  if (v === null || v === undefined) return <span className="text-slate-600 inline-flex items-center gap-1"><Minus className="h-3 w-3" /> n.d.</span>;
  if (v === "Sì" || v === "No") {
    const good = bad ? v === "No" : v === "Sì";
    return <span className={`inline-flex items-center gap-1 font-semibold ${good ? "text-emerald-400" : "text-rose-400"}`}>{good ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {v}</span>;
  }
  return <span className="text-white">{v}</span>;
};

const Row = ({ n, label, children }) => (
  <div className="grid grid-cols-[1.6rem_1fr_1.2fr] gap-2 py-1.5 border-b border-white/5 last:border-0 text-sm items-start">
    <span className="text-slate-600 font-mono text-xs pt-0.5">{n})</span>
    <span className="text-slate-400">{label}</span>
    <span className="text-right">{children}</span>
  </div>
);

const finRows = (fins) => (fins || []).filter(Boolean).map((f, i) => (
  <div key={`${f.tipo}-${i}`} className="text-xs text-right text-slate-200">
    <span className="font-bold text-accent">{f.tipo || "—"}</span> · inizio {f.inizio || "n.d."} · {f.durata_mesi ? `${f.durata_mesi} mesi` : "durata n.d."} · rata {eur(f.rata) || "n.d."} · {f.con_chi || "n.d."}
    {f.tipo !== "PP" && <> · rinnovabile <Val v={yn(f.rinnovabile)} /></>}
  </div>
));

export default function SchedaCliente({ lead }) {
  const s = lead.structured || {};
  const cat = (s.categoria || lead.derived?.categoria || "").toLowerCase();
  const c = s.contratto || {}, az = s.azienda || {}, tfr = s.tfr || {}, p = s.pensione || {}, inv = s.invalidita || {}, crif = s.crif || {}, an = s.anagrafica || {};
  const crifList = ["ritardi", "disguidi", "pignoramenti", "bocciature_recenti"].filter((k) => crif[k]).map((k) => k.replace("_", " "));
  const fins = (s.finanziamenti || []).filter(Boolean);
  const isPens = cat === "pensionato";
  const isPub = ["pubblico", "ministeriale", "militare", "para-pubblico"].includes(cat);
  const titolo = isPens ? "Pensionato" : isPub ? "Ministeriale / Pubblico" : "Privato";
  let n = 0; const next = () => ++n;

  return (
    <div data-testid="scheda-cliente" className="rounded-lg bg-slate-900 border border-accent/30 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase text-accent flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> Scheda cliente · {titolo}</div>
        <div className="text-xs text-slate-400">{s.eta ? `${s.eta} anni` : ""} {s.reddito_nucleo_familiare ? `· nucleo ${eur(s.reddito_nucleo_familiare)}` : ""}</div>
      </div>

      {isPens ? (<>
        <Row n={next()} label="Nome e cognome"><Val v={txt(an.nome) || lead.contatto?.nome} /></Row>
        <Row n={next()} label="Data e luogo di nascita"><Val v={[an.data_nascita, an.luogo_nascita].filter(Boolean).join(" · ") || null} /></Row>
        <Row n={next()} label="Finanziamenti in corso">{fins.length ? finRows(fins) : <Val v="No" bad />}</Row>
        <Row n={next()} label="Pensione netta e composizione"><Val v={[eur(p.importo || s.stipendio_netto), p.composizione && p.composizione.toUpperCase()].filter(Boolean).join(" · ") || null} /></Row>
      </>) : (<>
        <Row n={next()} label="Tempo indeterminato"><Val v={yn(c.tempo_indeterminato)} /></Row>
        <Row n={next()} label={isPub ? "Ente / amministrazione, dipendenti" : "Azienda, ragione sociale, dipendenti"}>
          <Val v={[az.nome || az.ragione_sociale, az.numero_dipendenti && `${az.numero_dipendenti} dip.`].filter(Boolean).join(" · ") || null} />
        </Row>
        <Row n={next()} label="Part time / Full time → stipendio"><Val v={[c.part_time === true ? "Part Time" : c.part_time === false ? "Full Time" : null, eur(s.stipendio_netto)].filter(Boolean).join(" → ") || null} /></Row>
        {(c.mansione || c.orario) && <Row n={next()} label="Mansione · orario"><Val v={[c.mansione, c.orario].filter(Boolean).join(" · ")} /></Row>}
        <Row n={next()} label={isPub ? "Data assunzione" : "Assunzione · TFR"}>
          <Val v={[c.data_assunzione, !isPub && (tfr.in_fondo ? `TFR in fondo ${tfr.nome_fondo || ""}`.trim() : tfr.in_azienda ? "TFR in azienda" : null), !isPub && tfr.prelievo_tfr && "prelievo TFR"].filter(Boolean).join(" · ") || null} />
        </Row>
        <Row n={next()} label="Finanziamenti in corso">{fins.length ? finRows(fins) : <Val v="No" bad />}</Row>
        <Row n={next()} label={isPub ? "Ente concede delega" : "Azienda concede delega"}><Val v={yn(az.concede_delega)} /></Row>
        {isPub && <Row n={next()} label="Anni mancanti alla pensione"><Val v={txt(p.anni_mancanti)} /></Row>}
      </>)}
      <Row n={next()} label="Invalidità → istanza">
        <Val v={inv.presente ? [inv.tipo || inv.tipo_cat, inv.percentuale && `${inv.percentuale}%`, inv.istanza ? "istanza presentata" : null, inv.oblio_oncologico ? "oblio oncologico" : null].filter(Boolean).join(" · ") || "Sì" : "No"} bad />
      </Row>
      <Row n={next()} label="CRIF (ritardi, disguidi, pignoramenti, bocciature)"><Val v={crifList.length ? crifList.join(", ") : "No"} bad /></Row>
      <Row n={next()} label="Liquidità e motivo"><Val v={[eur(s.importo_richiesto || lead.importo_desiderato), s.motivo_liquidita || s.finalita].filter(Boolean).join(" · ") || null} /></Row>
    </div>
  );
}
